/**
 * EntityDetailSheet — the single canonical entity inspector sheet.
 *
 * This is the sheet the Research results page opens when you click an entity;
 * every other "open an entity" surface (research citations, etc.) renders the
 * exact same component so the chrome, header, graph actions and body are
 * identical.
 *
 * Two entry points, one render:
 *  • `hit`    — a search EntityHit (carries label/name/props directly)
 *  • `nodeId` — a bare element id (citations / graph refs); the hit is
 *               resolved from the node so the sheet looks the same either way.
 *
 * Body is the shared `SidebarEntityDetail` (source docs · Properties /
 * Relationships / Neighbours). The header + graph actions live in the sheet
 * chrome here.
 */

import { Network, Plus } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  addContributionToActive,
  openAsExploration,
} from '@/components/agent/tools/_shared/applyContribution'
import { useDocumentOverlayOptional } from '@/components/document-overlay/DocumentDetailOverlayContext'
import { useEntityDetails } from '@/components/neo4j/shared/useEntityDetails'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { toast } from '@/components/ui/sonner'
import {
  buildGraphViewFromEntityHits,
  type EntityHit,
  getEntityHitDisplayName,
} from '@/services/api/case-search'
import { useInvestigationsStoreApiOptional } from '@/stores/investigations-store-provider' // frontend-rules-ok: owns graph-contribution IO (parity with the prior search entity sheet)
import { EntityDetailHeader } from './EntityDetailHeader'
import { SidebarEntityDetail } from './SidebarEntityDetail'

interface Props {
  caseId: string | null
  /** Open from a search hit (label/name/props known up front). */
  hit?: EntityHit | null
  /** Or open by element id; the hit is resolved from the node. */
  nodeId?: string | null
  onClose: () => void
}

export function EntityDetailSheet({ caseId, hit: hitProp = null, nodeId = null, onClose }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Outside a case's investigations (a story, a test) the sheet still reads; only the
  // graph actions need the store, so they are left out without one.
  const storeApi = useInvestigationsStoreApiOptional()

  const open = hitProp !== null || nodeId !== null

  // Close the sheet when a source-document overlay opens from inside it — the
  // modal sheet would otherwise trap pointer events above the overlay.
  const overlay = useDocumentOverlayOptional()
  const overlayOpen = Boolean(overlay?.isOpen)
  useEffect(() => {
    if (open && overlayOpen) onClose()
  }, [open, overlayOpen, onClose])

  // nodeId entry point: resolve the node so we can build a hit-equivalent and
  // render identically to the search-hit path.
  const probe = useMemo(
    () =>
      !hitProp && nodeId && caseId ? { id: nodeId, name: '', labels: [], properties: {} } : null,
    [hitProp, nodeId, caseId]
  )
  const { fullNodeDetails } = useEntityDetails({
    caseId: caseId ?? '',
    selectedNode: probe,
    selectedEdge: null,
  })

  const hit: EntityHit | null = useMemo(() => {
    if (hitProp) return hitProp
    if (!fullNodeDetails) return null
    const labels = fullNodeDetails.labels ?? []
    return {
      entity_id: fullNodeDetails.id,
      entity_label:
        fullNodeDetails.type ??
        labels.find(l => !l.startsWith('__') && l !== 'Node') ??
        labels[0] ??
        '',
      entity_name: fullNodeDetails.name ?? null,
      entity_props: fullNodeDetails.properties ?? {},
      match_field: '',
      match_value: '',
      score: 0,
      document_id: null,
      document_name: null,
      chunk_id: null,
      page_number: null,
    }
  }, [hitProp, fullNodeDetails])

  const bodyNodeId = hit?.entity_id ?? nodeId
  const name = hit ? getEntityHitDisplayName(hit) : ''

  const goToGraph = (mode: 'show' | 'add') => {
    if (!hit || !caseId || !storeApi) return
    const label = name || hit.entity_id
    const view = buildGraphViewFromEntityHits([hit], caseId, label)
    const result =
      mode === 'show'
        ? openAsExploration({ store: storeApi, view, label })
        : addContributionToActive({ store: storeApi, view, label })
    if (mode === 'show' || result.contributionId) {
      toast.success(t('research.detail.addedToGraphToast', { name: label }))
      onClose()
      navigate(`/cases/${caseId}/graph`)
    } else {
      toast.message(t('research.detail.alreadyInGraph'))
    }
  }

  return (
    <Sheet open={open} onOpenChange={next => !next && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[440px]">
        <SheetHeader className="gap-3 border-b border-border/60 px-4 py-4">
          {/* Accessible dialog title; the visible identity is EntityDetailHeader. */}
          <SheetTitle className="sr-only">
            {name || t('investigations.entityDetail.title')}
          </SheetTitle>
          <EntityDetailHeader
            name={name || t('research.detail.untitled')}
            typeLabel={hit?.entity_label}
          />
          {hit && caseId && storeApi ? (
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" className="gap-1.5" onClick={() => goToGraph('show')}>
                <Network className="size-3.5" />
                {t('investigations.entityDetail.showOnGraph')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => goToGraph('add')}
              >
                <Plus className="size-3.5" />
                {t('investigations.entityDetail.addToGraph')}
              </Button>
            </div>
          ) : null}
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {bodyNodeId && caseId ? (
            <SidebarEntityDetail caseId={caseId} nodeId={bodyNodeId} />
          ) : (
            <p className="px-4 py-4 text-sm italic text-muted-foreground">
              {t('investigations.entityDetail.openInCase')}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
